import { createI18n } from "vue-i18n"
import de from "./de"
import en from "./en"
import { applyToBundle, termsFor } from "@/services/terminology"

const defaultLocale = "de"

// The bundles as written, with their `[[hwr]]`-style term tokens still in them. vue-i18n only ever
// sees resolved copies (see `applyTerminology`), so these stay untouched for the next resolution.
const bundles = { de, en }

function resolvedBundles(overrides) {
  return Object.fromEntries(
    Object.entries(bundles).map(([locale, bundle]) => [locale, applyToBundle(bundle, termsFor(locale, overrides))])
  )
}

const i18n = createI18n({
  legacy: false,
  locale: localStorage.getItem("locale") ?? defaultLocale,
  fallbackLocale: defaultLocale,
  warnHtmlMessage: false,
  escapeParameter: true,
  // The default names until the workspace's settings arrive — which is before the first mount for a
  // signed-in session (main.js awaits them), so nobody sees one name turn into another.
  messages: resolvedBundles([]),
})

let appliedOverrides = "[]"

/**
 * Re-resolves every bundle against the workspace's names (`settings.terminology`). Called by the
 * settings store whenever the settings change hands — load, save, sign-in, sign-out — and a no-op
 * when the names are the ones already applied, which is nearly every time.
 */
export function applyTerminology(overrides) {
  const fingerprint = JSON.stringify(overrides ?? [])
  if (fingerprint === appliedOverrides) return
  appliedOverrides = fingerprint
  for (const [locale, messages] of Object.entries(resolvedBundles(overrides ?? []))) {
    i18n.global.setLocaleMessage(locale, messages)
  }
}

/**
 * A message as written, tokens included — for the one place that resolves it against names nobody
 * has saved yet: the preview on the Bezeichnungen tab.
 */
export function rawMessage(locale, path) {
  return path.split(".").reduce((node, key) => node?.[key], bundles[locale])
}

export default i18n
